'use client'
import type { FC } from 'react'
import styled from 'styled-components'
import { MODALS } from '@/context'
import { useModal, useTranslator } from '@/hooks'
import { ADD_TO_CART_TRANSLATION, getDynamicText, getIcon, IS_HM_STORE, LANG } from '@/utils'
import { Button } from '../../base/Button'
import { E_DELIVERY_TYPE, TypeCmsProduct, TypeCmsVariant } from '@/schema'
import { Img } from '@/components/base'
import { mq, vw } from '@/styles'

type Props = {
  data: {
    product: TypeCmsProduct
    variant: TypeCmsVariant
    name: TypeCmsProduct['name']
    brand: TypeCmsProduct['brand']
    categories: TypeCmsProduct['categories']
    stock?: TypeCmsVariant['stock']
    inventoryTracking?: TypeCmsProduct['inventoryTracking']
  }
  justIcon?: boolean
}

export const ButtonAddPerBox: FC<Props> = ({ data, justIcon }) => {
  const { name, brand, categories, variant, product, stock, inventoryTracking } = data
  const { updatePayload } = useModal(MODALS.CART_CONTROLS)
  const { t } = useTranslator()

  return (
    <ButtonStyled $justIcon={justIcon} type='button' outline={true} onClick={() => updatePayload({
      payload: {
        data: { name, brand, categories, variant, product },
        stock,
        inventoryTracking
      },
      open: true
    })} aria-label={ADD_TO_CART_TRANSLATION[LANG]} bgColor = '--color-text' color='--color-text'>
      <Img media={getIcon(`cart-controls-box${variant?.deliveryType === E_DELIVERY_TYPE.FROZEN ? '--frozen' : IS_HM_STORE ? '--hm' : ''}`)} />
      <span>{getDynamicText(t('project.product.cart_controls') as string, { units: variant.qtyPerBox })}</span>
    </ButtonStyled>
  )
}

const ButtonStyled = styled(Button)<{ $justIcon?: boolean }>`
  flex-shrink: 0;
  height: ${({ $justIcon }) => $justIcon ? '30px' : '28px'};
  margin: 0 8px;
  padding: ${({ $justIcon }) => $justIcon ? '2px' : '3px 13px'};
  white-space: nowrap;
  width: ${({ $justIcon }) => $justIcon ? '30px' : 'auto'};

  ${mq.greaterThan('tablet')} {
    height: ${({ $justIcon }) => $justIcon ? vw(44, 'desktop') : '28px'};
    width: ${({ $justIcon }) => $justIcon ? vw(44, 'desktop') : 'auto'};
  }

  ${mq.greaterThan('desktop')} {
    height: ${({ $justIcon }) => $justIcon ? '44px' : '28px'};
    width: ${({ $justIcon }) => $justIcon ? '44px' : 'auto'};
  }

  > span {
    align-items: center;
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
    justify-content: center;

    > span {
      display: ${({ $justIcon }) => $justIcon ? 'none' : 'flex'};
    }
  }

  img {
    flex-shrink: 0;
    width: 15px;

    ${mq.greaterThan('tablet')} {
      width: ${({ $justIcon }) => $justIcon ? vw(20, 'desktop') : '15px'};
    }

    ${mq.greaterThan('desktop')} {
      width: ${({ $justIcon }) => $justIcon ? '20px' : '15px'};
    }
  }

  &:hover {
    > div {
      transform: ${({ $justIcon }) => $justIcon ? 'scale3d(1.2, 1.2, 1)' : 'scale3d(1.1, 1.2, 1)'};
    }
  }
`
